import { AIStarIcon } from '@app/components/icons/ai-star';
import { Tooltip } from '@app/components/tooltip';
import { useAppDispatch, useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useCreatorLabAnalytics } from '../../../routes/lab/hooks';
import { getContentTypeTranslationKey } from '../../../routes/lab/utils';
import { ContentType } from '../domain';
import {
  selectContentComposerContentRepurposingState,
  selectContentComposerEmailAudienceConfig,
  selectContentComposerGenerationAttributes,
  selectContentComposerToneOfVoice,
  selectContentComposerToneOfVoiceConfig,
  selectIsContentGenerationBlockedForType,
} from '../selector';
import { type SelectorOption, Selector } from './selector';

export interface GenerationHeaderProps {
  contentType: ContentType;
  className?: string;
}

/**
 * Hook to transform and prepare all selector options and selected values
 */
const useGenerationHeaderData = (contentType: ContentType) => {
  const { t } = useTranslation();

  const toneOfVoiceConfig = useAppSelector(
    selectContentComposerToneOfVoiceConfig,
  );
  const selectedToneId = useAppSelector(selectContentComposerToneOfVoice);
  const contentRepurposingState = useAppSelector(
    selectContentComposerContentRepurposingState,
  );
  // Get generation attributes (shared across all content types)
  const generationAttributes = useAppSelector(
    selectContentComposerGenerationAttributes,
  );
  const emailAudienceConfig = useAppSelector(
    selectContentComposerEmailAudienceConfig,
  );

  // Extract topics from content repurposing state
  const topics = useMemo(() => {
    if (
      contentRepurposingState.status !== 'success' ||
      !contentRepurposingState.data ||
      contentRepurposingState.data.eligibility !== 'ELIGIBLE' ||
      contentRepurposingState.data.topics.status !== 'FINISHED' ||
      !contentRepurposingState.data.topics.value
    ) {
      return [];
    }
    return contentRepurposingState.data.topics.value;
  }, [contentRepurposingState]);

  // Transform tone options
  const toneOptions: SelectorOption[] = useMemo(
    () =>
      toneOfVoiceConfig.status === 'success' && toneOfVoiceConfig.data
        ? toneOfVoiceConfig.data.map(option => ({
            value: option.key,
            label: t(
              `content-recording.content-composer.tone-of-voice.options.${option.key}`,
              { defaultValue: option.toneOfVoice },
            ),
          }))
        : [],
    [toneOfVoiceConfig, t],
  );

  // Transform topic options
  const topicOptions: SelectorOption[] = useMemo(
    () =>
      topics.map(topic => ({
        value: topic.id,
        label: topic.topic,
      })),
    [topics],
  );

  // Transform email audience options
  const emailAudienceOptions: SelectorOption[] = useMemo(() => {
    if (emailAudienceConfig.status !== 'success' || !emailAudienceConfig.data) {
      return [];
    }
    return emailAudienceConfig.data.map(audience => ({
      value: audience.key,
      label: t(audience.labelKey),
    }));
  }, [emailAudienceConfig, t]);

  // Transform email type options based on selected audience
  const emailTypeOptions: SelectorOption[] = useMemo(() => {
    if (contentType !== 'EMAIL') {
      return [];
    }
    const selectedAudience = emailAudienceConfig.data?.find(
      audience => audience.key === generationAttributes.emailAudienceId,
    );
    if (!selectedAudience) {
      return [];
    }
    return selectedAudience.types.map(type => ({
      value: type.key,
      label: t(type.labelKey),
    }));
  }, [contentType, generationAttributes, emailAudienceConfig, t]);

  // Extract selected values (shared across all content types)
  const selectedTopicId = generationAttributes.topicId;
  const selectedEmailAudienceId = generationAttributes.emailAudienceId;
  const selectedEmailTypeId = generationAttributes.emailTypeId;

  return {
    toneOptions,
    topicOptions,
    emailAudienceOptions,
    emailTypeOptions,
    selectedToneId,
    selectedTopicId,
    selectedEmailAudienceId,
    selectedEmailTypeId,
    topics,
    generationAttributes,
  };
};

/**
 * Hook to handle all action dispatches for generation header
 */
const useGenerationHeaderActions = (
  contentType: ContentType,
  toneOptions: SelectorOption[],
  topicOptions: SelectorOption[],
) => {
  const dispatch = useAppDispatch();
  const {
    trackToneSelected,
    trackTopicSelected,
    trackEmailRecipientSelected,
    trackEmailTypeSelected,
    trackGenerateOrRegenerate,
  } = useCreatorLabAnalytics();

  const handleToneChange = useCallback(
    (toneId: string) => {
      const toneName =
        toneOptions.find(opt => opt.value === toneId)?.label || toneId;
      trackToneSelected(toneName);

      dispatch({
        type: '[content-composer] tone selected',
        payload: { toneId },
      });
    },
    [dispatch, trackToneSelected, toneOptions],
  );

  const handleTopicChange = useCallback(
    (topicId: string) => {
      const topicName =
        topicOptions.find(opt => opt.value === topicId)?.label || topicId;
      trackTopicSelected(topicName);

      dispatch({
        type: '[content-composer] topic selected',
        payload: { contentType, topicId },
      });
    },
    [dispatch, contentType, trackTopicSelected, topicOptions],
  );

  const handleEmailAudienceChange = useCallback(
    (audienceId: string) => {
      trackEmailRecipientSelected(audienceId);

      dispatch({
        type: '[content-composer] email audience selected',
        payload: { audienceId },
      });
    },
    [dispatch, trackEmailRecipientSelected],
  );

  const handleEmailTypeChange = useCallback(
    (emailTypeId: string) => {
      trackEmailTypeSelected(emailTypeId);

      dispatch({
        type: '[content-composer] email type selected',
        payload: { emailTypeId },
      });
    },
    [dispatch, trackEmailTypeSelected],
  );

  const handleGenerate = useCallback(() => {
    trackGenerateOrRegenerate();

    dispatch({
      type: '[content-composer] generate content',
      payload: { contentType },
    });
  }, [dispatch, contentType, trackGenerateOrRegenerate]);

  return {
    handleToneChange,
    handleTopicChange,
    handleEmailAudienceChange,
    handleEmailTypeChange,
    handleGenerate,
  };
};

export const GenerationHeader: React.FC<GenerationHeaderProps> = ({
  contentType,
  className = '',
}) => {
  const { t } = useTranslation();
  const contentTypeKey = getContentTypeTranslationKey(contentType);

  // Get all data and options
  const {
    toneOptions,
    topicOptions,
    emailAudienceOptions,
    emailTypeOptions,
    selectedToneId,
    selectedTopicId,
    selectedEmailAudienceId,
    selectedEmailTypeId,
    topics,
    generationAttributes,
  } = useGenerationHeaderData(contentType);

  // Get all action handlers
  const {
    handleToneChange,
    handleTopicChange,
    handleEmailAudienceChange,
    handleEmailTypeChange,
    handleGenerate,
  } = useGenerationHeaderActions(contentType, toneOptions, topicOptions);

  // Set topicId to first topic (overall summary) when switching to EMAIL type
  useEffect(() => {
    if (
      contentType === 'EMAIL' &&
      !generationAttributes.topicId &&
      topics.length > 0
    ) {
      handleTopicChange(topics[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType]);

  // Check if generation is blocked for this content type (disables Generate button)
  const isGenerating = useAppSelector(state =>
    selectIsContentGenerationBlockedForType(state, contentType),
  );

  // Note: Auto-selection of default values (tone, topic, email audience/type) is handled
  // in the Redux reducer when configs load successfully. However, when switching to EMAIL
  // content type, we need to ensure topicId is set to the first topic (overall summary).

  return (
    <div className={cx('flex flex-col gap-4', className)}>
      <div className="flex items-start gap-2">
        {/* Tone of Voice */}
        <Selector
          label={t(
            'content-recording.content-composer.generation-header.tone-of-voice',
          )}
          options={toneOptions}
          selectedValue={selectedToneId}
          onChange={handleToneChange}
          dataTestId="generation-header-tone-selector"
          className="flex-1"
          buttonClassName="h-8 px-2 gap-1"
          useTooltipForOptions
        />
        {/* Topic */}
        {['SOCIAL_POST', 'BLOG_POST'].includes(contentType) && (
          <Selector
            label={t(
              'content-recording.content-composer.generation-header.topic',
            )}
            options={topicOptions}
            selectedValue={selectedTopicId ?? ''}
            onChange={handleTopicChange}
            dataTestId="generation-header-topic-selector"
            className="flex-1"
            disabled={topicOptions.length === 0}
            buttonClassName="h-8 px-2 gap-1"
            useTooltipForOptions
          />
        )}
        {contentType === 'EMAIL' && (
          <>
            <Selector
              label={t(
                'content-recording.content-composer.generation-header.recipient',
              )}
              options={emailAudienceOptions}
              selectedValue={selectedEmailAudienceId ?? ''}
              onChange={handleEmailAudienceChange}
              dataTestId="generation-header-recipient-selector"
              className="flex-1"
              disabled={emailAudienceOptions.length === 0}
              buttonClassName="h-8 px-2 gap-1"
            />

            {/* Subject (Email Type) */}
            <Selector
              label={t(
                'content-recording.content-composer.generation-header.subject',
              )}
              options={emailTypeOptions}
              selectedValue={selectedEmailTypeId ?? ''}
              onChange={handleEmailTypeChange}
              dataTestId="generation-header-subject-selector"
              className="flex-1"
              disabled={
                emailTypeOptions.length === 0 || !selectedEmailAudienceId
              }
              buttonClassName="h-8 px-2 gap-1"
              useTooltipForOptions
            />
          </>
        )}
      </div>
      <div className="flex items-center justify-start gap-3">
        <p className="m-0 text-[17px] font-medium leading-[1.47] text-white">
          {t(
            `content-recording.content-composer.content-types.${contentTypeKey}.title`,
          )}
        </p>
        <Tooltip
          tooltip={
            isGenerating
              ? t('content-recording.lab.content-generation-in-progress')
              : ''
          }
          shouldShow={isGenerating}
          placement="bottom-center"
        >
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !selectedTopicId}
            className={cx(
              'h-8 px-3 flex items-center gap-2 rounded-[4px] border border-spring-neutral-b0/20 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 bg-spring-neutral-base',
              {
                'opacity-50 cursor-not-allowed':
                  isGenerating || !selectedTopicId,
                'hover:bg-spring-neutral-base/10':
                  !isGenerating && selectedTopicId,
              },
            )}
            data-testid="generation-header-generate-button"
          >
            <AIStarIcon className="size-4" />
            <span className="text-white text-sm font-medium">
              {t(
                'content-recording.content-composer.generation-header.generate',
              )}
            </span>
          </button>
        </Tooltip>
      </div>
    </div>
  );
};
